<template>
  <div class="projects-overview" data-test="projects-overview">
    <h4 class="po-title">
      Project files
      <span class="po-count">{{ countLabel }}</span>
    </h4>
    <p class="po-hint">Choose a project to see its files.</p>

    <b-form-input
      v-if="projects.length > SEARCH_THRESHOLD"
      v-model="search"
      class="po-search"
      placeholder="Search projects..."
      aria-label="Search projects"
      data-test="overview-search"
    />

    <div v-if="projects.length > 1" class="po-sort" data-test="overview-sort">
      <span class="po-sort-label">Sort</span>
      <button
        v-for="option in sortOptions"
        :key="option.id"
        type="button"
        class="po-sort-option"
        :class="{ active: sort === option.id }"
        :aria-pressed="sort === option.id ? 'true' : 'false'"
        :data-test="`sort-${option.id}`"
        @click="sort = option.id"
      >
        {{ option.label }}
      </button>
    </div>

    <div v-for="group in groups" :key="group.key" class="po-group">
      <h5 v-if="groups.length > 1" class="po-group-title">
        {{ group.title }}
        <span class="po-group-count">{{ group.items.length }}</span>
      </h5>
      <div class="po-list">
        <button
          v-for="project in group.items"
          :key="project.id"
          type="button"
          class="po-row"
          data-test="overview-project"
          @click="$emit('open', project.id)"
        >
          <b-icon
            :icon="project.readOnly ? 'people' : 'building'"
            class="po-icon"
            aria-hidden="true"
          />
          <span class="po-text">
            <span class="po-name"
              >{{ project.label
              }}<template v-if="project.archived && groups.length === 1">
                (archived)</template
              ></span
            >
            <span v-if="project.readOnly" class="po-sub">
              Shared by {{ project.sharedBy || 'another company' }}
            </span>
          </span>
          <span :class="['po-files', { 'is-empty': project.count === 0 }]">
            {{ filesLabel(project.count) }}
          </span>
          <b-icon icon="chevron-right" class="po-chevron" aria-hidden="true" />
        </button>
      </div>
    </div>

    <p v-if="!groups.length" class="po-empty" data-test="overview-empty">
      No projects match.
    </p>
  </div>
</template>

<script>
// What the right side shows when the sidebar is on its Projects tab: every
// project the person can open, to pick from. (The project's own files open from here.)
const byName = (a, b) =>
  a.label.localeCompare(b.label, undefined, {
    numeric: true,
    sensitivity: 'base',
  })

export default {
  props: {
    // { id: 'project:<id>', label, count, readOnly, sharedBy }
    projects: { type: Array, default: () => [] },
  },

  data() {
    return {
      search: '',
      sort: 'name',
      SEARCH_THRESHOLD: 6,
      sortOptions: [
        { id: 'name', label: 'A–Z' },
        { id: 'files', label: 'Most files' },
      ],
    }
  },

  computed: {
    countLabel() {
      const n = this.projects.length
      return `${n} ${n === 1 ? 'project' : 'projects'}`
    },

    visible() {
      const term = this.search.trim().toLowerCase()
      const matches = term
        ? this.projects.filter((project) =>
            project.label.toLowerCase().includes(term),
          )
        : this.projects.slice()
      // "project 2" before "project 10"; most files first, then by name.
      return matches.sort((a, b) =>
        this.sort === 'files' && a.count !== b.count
          ? b.count - a.count
          : byName(a, b),
      )
    },

    // The company's own projects first, then the ones shared in (view only), then archived ones.
    groups() {
      const live = this.visible.filter((project) => !project.archived)
      return [
        {
          key: 'own',
          title: 'Your projects',
          items: live.filter((project) => !project.readOnly),
        },
        {
          key: 'shared',
          title: 'Shared with you',
          items: live.filter((project) => project.readOnly),
        },
        {
          key: 'archived',
          title: 'Archived',
          items: this.visible.filter((project) => project.archived),
        },
      ].filter((group) => group.items.length)
    },
  },

  methods: {
    filesLabel(count) {
      return `${count} ${count === 1 ? 'file' : 'files'}`
    },
  },
}
</script>

<style scoped>
.po-title {
  margin-bottom: 0;
  color: #111827;
  font-weight: 700;
}

.po-count {
  margin-left: 8px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 400;
}

.po-hint {
  margin: 4px 0 16px;
  color: #6b7280;
  font-size: 14px;
}

.po-search {
  margin-bottom: 12px;
}

/* A / Z and Most files: the same pill look as the file type chips. */
.po-sort {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
}

.po-sort-label {
  margin-right: 2px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.po-sort-option {
  padding: 3px 12px;
  background-color: #e9ecef;
  border: 1px solid #dcdddd;
  border-radius: 999px;
  color: #212529;
  font-family: inherit;
  font-size: 13px;
}

.po-sort-option:hover {
  background-color: #dee2e6;
}

.po-sort-option.active {
  background-color: #006cff;
  border-color: #006cff;
  color: #ffffff;
}

.po-group + .po-group {
  margin-top: 24px;
}

.po-group-title {
  margin-bottom: 8px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.po-group-count {
  margin-left: 6px;
  font-weight: 400;
}

/* One bordered list, a row per project, the counts lined up on the right. */
.po-list {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.po-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  padding: 8px 14px;
  background-color: #ffffff;
  border: 0;
  border-top: 1px solid #e5e7eb;
  color: #111827;
  font-family: inherit;
  text-align: left;
}

.po-row:first-child {
  border-top: 0;
}

.po-row:hover,
.po-row:focus-visible {
  background-color: #f9fafb;
  outline: none;
}

.po-row:focus-visible {
  box-shadow: inset 0 0 0 2px #006cff;
}

.po-icon {
  flex-shrink: 0;
  color: #9ca3af;
  font-size: 18px;
}

.po-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.po-name {
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.po-sub {
  color: #6b7280;
  font-size: 12px;
}

.po-files {
  flex-shrink: 0;
  color: #6b7280;
  font-size: 14px;
  text-align: right;
}

.po-files.is-empty {
  color: #d1d5db;
}

.po-chevron {
  flex-shrink: 0;
  color: #d1d5db;
  font-size: 12px;
}

.po-row:hover .po-chevron {
  color: #006cff;
}

.po-empty {
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 14px;
}
</style>
